import React from 'react';
import { FareResponse } from '../../types/fare';
import { isSingleModeFare, isBothModesFare, hasRouteInfo } from '../../types/fare';
import { SheetState } from '../../hooks/useBottomSheet';
import FareCard from './FareCard';
import PriceRangeBar from './PriceRangeBar';
import ModeComparisonCard from './ModeComparisonCard';
import RouteInfoSection from './RouteInfoSection';
import ContextCards from './ContextCards';
import { AlertCircle, Loader2 } from 'lucide-react';

interface FareResultsPanelProps {
  fareData: FareResponse | null;
  loading: boolean;
  error: string | null;
  selectedRouteIndex: number;
  onRouteSelect: (index: number) => void;
  sheetState?: SheetState; // Optional for desktop
}

export default function FareResultsPanel({
  fareData,
  loading,
  error,
  selectedRouteIndex,
  onRouteSelect,
  sheetState,
}: FareResultsPanelProps) {
  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 size={48} className="text-green-500 animate-spin mb-4" />
        <p className="text-gray-600">Calculating fare...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <AlertCircle size={24} className="text-red-600 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-red-900 mb-1">Error</h3>
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (!fareData) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="text-6xl mb-4">🚖</div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Select Locations to Calculate Fare
        </h3>
        <p className="text-sm text-gray-600 max-w-sm">
          Enter pickup and destination locations, or use quick routes to get started
        </p>
      </div>
    );
  }

  // Check if we're in full state (desktop or full mobile)
  // In half state, show only main card + price bar
  // In full state, show all content
  const isFullState = sheetState === 'full' || !sheetState; // Desktop or full mobile
  const isMobile = sheetState !== undefined; // Has sheetState = mobile

  // Results display
  return (
    <div className="space-y-2 md:space-y-6">
      {/* Single mode fare card - Always show */}
      {isSingleModeFare(fareData) && (
        <>
          <FareCard
            fare={fareData.fare}
            distanceKm={fareData.distanceKm}
            exchangeRates={fareData.exchangeRates}
          />
          <PriceRangeBar fare={fareData.fare} />
        </>
      )}

      {/* Both modes - Different display for mobile vs desktop */}
      {isBothModesFare(fareData) && (
        <>
          {/* Mobile: Show two stacked FareCards */}
          {isMobile ? (
            <>
              {/* Tuk-Tuk Card */}
              <FareCard
                fare={fareData.modes.tuk}
                distanceKm={fareData.distanceKm}
                exchangeRates={fareData.exchangeRates}
                mode="tuk"
              />
              
              {/* Taxi Card */}
              <FareCard
                fare={fareData.modes.taxi}
                distanceKm={fareData.distanceKm}
                exchangeRates={fareData.exchangeRates}
                mode="taxi"
              />
              
              {/* Comparison info below */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-center">
                <div className="text-sm text-purple-800">
                  💡 Taxi costs <span className="font-semibold">Rs. {(fareData.premium || 0).toLocaleString()}</span> more on average
                </div>
              </div>
            </>
          ) : (
            /* Desktop: Show ModeComparisonCard */
            <ModeComparisonCard
              modes={fareData.modes}
              premium={fareData.premium || 0}
            />
          )}
        </>
      )}

      {/* Route information - Only show in full state */}
      {isFullState && hasRouteInfo(fareData) && (
        <RouteInfoSection
          routeInfo={fareData.route}
          selectedRouteIndex={selectedRouteIndex}
          onRouteSelect={onRouteSelect}
          weatherMultiplier={fareData.weather.appliedMultiplier}
        />
      )}

      {/* Context cards - Only show in full state */}
      {isFullState && <ContextCards fareData={fareData} />}

      {/* Additional suggestions - Only show in full state */}
      {isFullState && fareData.offerTuk && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
          💡 Consider a tuk-tuk for shorter distances to save money
        </div>
      )}
      {isFullState && fareData.offerTaxi && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
          💡 For this distance, a taxi is recommended for comfort and safety
        </div>
      )}
    </div>
  );
}

